import React, { useState } from "react";

// Alcuni file cliente hanno sopra la vera intestazione una riga "di comodo"
// (titolo di una tabella pivot, riepilogo, righe vuote...): il backend se ne
// accorge confrontando quante celle hanno le prime righe (vedi routes/api.js
// /upload) e, se la prima riga sembra sospetta, chiede qui quale riga
// contiene davvero i nomi delle colonne, mostrando un'anteprima grezza delle
// prime righe del foglio. Stesso linguaggio visivo di SceltaFoglioModal.
//
// "Intestazioni su due righe" (checkbox): alcuni file (es. ordini con le
// taglie su colonne separate) hanno un'etichetta di GRUPPO unita
// orizzontalmente su una riga (es. "ITALY SIZE" su tutte le colonne taglia)
// e il nome VERO di ogni colonna in quella zona solo nella riga sotto (es.
// "35", "35.5", "36"...) — mentre le altre colonne (non sotto quel gruppo)
// hanno il loro nome vero già nella riga principale, e restano vuote in
// quella secondaria. Scegliendo anche una "riga specifica", il backend
// combina le due: per ogni colonna usa il valore della riga specifica se
// non è vuoto, altrimenti quello della riga principale — non serve più
// preparare a mano una copia del file con l'intestazione unificata.
export default function SceltaIntestazioneModal({ nomeFile, righeAnteprima, onScegli, onAnnulla }) {
  const [scelta, setScelta] = useState(0);
  const [dueRighe, setDueRighe] = useState(false);
  const [secondaria, setSecondaria] = useState(1);
  const numeroColonne = Math.max(...righeAnteprima.map((r) => r.length), 1);
  const indici = Array.from({ length: numeroColonne }, (_, i) => i);

  // Colonne "ambigue": la riga principale E quella specifica hanno ENTRAMBE
  // un valore non vuoto nella stessa colonna — capita con un'etichetta di
  // gruppo unita (es. "ITALY SIZE" scritta una volta sola nella prima cella
  // del gruppo) che occupa esattamente la colonna dove la riga specifica
  // avrebbe invece il nome vero della prima colonna del gruppo (es. "35").
  // A seconda di quale riga si sceglie come "specifica" cambia quale delle
  // due vince (la specifica, se non vuota, vince sempre) — la scelta
  // sbagliata fa sparire silenziosamente il nome vero, sostituito
  // dall'etichetta di gruppo, senza nessun errore visibile qui. Live,
  // ricalcolato ad ogni click, così si vede SUBITO prima di confermare.
  const rigaPrincipaleAttuale = righeAnteprima[scelta] || [];
  const rigaSecondariaAttuale = dueRighe && secondaria !== scelta ? (righeAnteprima[secondaria] || []) : null;
  const colonneAmbigue = rigaSecondariaAttuale
    ? indici.filter((i) => String(rigaPrincipaleAttuale[i] ?? "").trim() !== "" && String(rigaSecondariaAttuale[i] ?? "").trim() !== "")
    : [];

  // Intestazioni duplicate dopo la combinazione (principale + eventuale
  // specifica): due colonne con lo stesso nome finale — anche senza due
  // righe, es. due colonne del file letteralmente chiamate uguali — fanno sì
  // che i dati della prima vengano sovrascritti da quelli della seconda
  // quando la riga si trasforma in un oggetto {nomeColonna: valore}.
  const headerRisolti = indici.map((i) => {
    const spec = rigaSecondariaAttuale ? String(rigaSecondariaAttuale[i] ?? "").trim() : "";
    return spec || String(rigaPrincipaleAttuale[i] ?? "").trim();
  });
  const conteggioHeaderRisolti = {};
  headerRisolti.forEach((h) => { if (h) conteggioHeaderRisolti[h] = (conteggioHeaderRisolti[h] || 0) + 1; });
  const intestazioniDuplicate = [...new Set(headerRisolti.filter((h) => h && conteggioHeaderRisolti[h] > 1))];

  function confermaDueRighe(v) {
    setDueRighe(v);
    if (v && secondaria === scelta) setSecondaria(Math.min(scelta + 1, righeAnteprima.length - 1));
  }

  function confermaScelta() {
    const secondariaValida = dueRighe && secondaria !== scelta ? secondaria : null;
    onScegli(scelta, secondariaValida);
  }

  return (
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "Inter, sans-serif" }}
      onClick={onAnnulla}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: "var(--cf-bg-card)", borderRadius: "16px", width: "820px", maxWidth: "94vw", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.45)" }}
      >
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px", lineHeight: 1 }}>📋</span>
            <div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontWeight: 700, fontSize: "15px", letterSpacing: "1px", textTransform: "uppercase", color: "#f1f5f9" }}>
                Scegli la riga delle intestazioni
              </div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "11px", color: "var(--cf-text-faint)", letterSpacing: "0.3px", marginTop: "3px" }}>
                {nomeFile}
              </div>
            </div>
          </div>
          <button className="cf-btn" onClick={onAnnulla} style={{ border: "none", background: "rgba(255,255,255,0.08)", borderRadius: "6px", width: "28px", height: "28px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", color: "#cbd5e1", lineHeight: 1 }}>
            ✕
          </button>
        </div>

        <div style={{ padding: "24px", overflowY: "auto" }}>
          <p className="testo-secondario" style={{ marginTop: 0 }}>
            La prima riga di questo file non sembra contenere i nomi delle colonne (es. un titolo o una riga vuota
            sopra la vera intestazione). Clicca sulla riga (colonna "principale") che contiene i nomi reali.
          </p>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px", cursor: "pointer" }}>
            <input type="checkbox" checked={dueRighe} onChange={(e) => confermaDueRighe(e.target.checked)} />
            Le intestazioni sono divise su due righe (es. un'etichetta di gruppo sopra — tipo "ITALY SIZE" unita su
            più colonne — e il nome vero di ogni colonna, come le taglie, solo nella riga sotto)
          </label>
          {dueRighe && (
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "10px" }}>
              Clicca anche la riga "specifica" qui sotto (seconda colonna di pallini): per ogni colonna verrà usato
              il suo valore se non è vuoto, altrimenti quello della riga principale. La riga "specifica" è quella con
              i nomi più DETTAGLIATI (es. le taglie "35", "35.5"...), la "principale" è quella con le etichette più
              generiche (es. "ITALY SIZE") — se le inverti, un'etichetta generica può sostituire per sbaglio il nome
              vero di una colonna, senza nessun errore visibile.
            </p>
          )}
          {colonneAmbigue.length > 0 && (
            <div className="errore" style={{ marginBottom: "10px" }}>
              ⚠️ {colonneAmbigue.length} colonna/e hanno un valore in ENTRAMBE le righe scelte — controlla che abbia
              vinto quella giusta (di norma il nome più specifico, non l'etichetta di gruppo):
              <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.2rem" }}>
                {colonneAmbigue.slice(0, 8).map((i) => (
                  <li key={i}>
                    Colonna {i + 1}: principale = "{String(rigaPrincipaleAttuale[i] ?? "")}", specifica = "
                    {String(rigaSecondariaAttuale[i] ?? "")}" → vince "{headerRisolti[i]}"
                  </li>
                ))}
              </ul>
              {colonneAmbigue.length > 8 && (
                <p style={{ margin: "0.4rem 0 0" }}>...e altre {colonneAmbigue.length - 8} colonna/e.</p>
              )}
            </div>
          )}
          {intestazioniDuplicate.length > 0 && (
            <p className="errore" style={{ marginTop: 0, marginBottom: "10px" }}>
              ⚠️ Dopo la combinazione, queste colonne risultano con lo stesso nome:{" "}
              <strong>{intestazioniDuplicate.join(", ")}</strong> — i dati della prima verranno sovrascritti da
              quelli dell'ultima con lo stesso nome.
            </p>
          )}
          <div className="scroll-orizzontale">
            <table className="tabella-anteprima">
              <tbody>
                {righeAnteprima.map((riga, i) => (
                  <tr
                    key={i}
                    onClick={() => setScelta(i)}
                    style={{ cursor: "pointer", backgroundColor: scelta === i || (dueRighe && secondaria === i) ? "rgba(30, 58, 138, 0.15)" : undefined }}
                  >
                    <td style={{ whiteSpace: "nowrap" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }} onClick={(e) => e.stopPropagation()}>
                        <input type="radio" name="riga-intestazione" checked={scelta === i} onChange={() => setScelta(i)} />
                        riga {i + 1} (principale)
                      </label>
                    </td>
                    {dueRighe && (
                      <td style={{ whiteSpace: "nowrap" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }} onClick={(e) => e.stopPropagation()}>
                          <input type="radio" name="riga-intestazione-secondaria" checked={secondaria === i} onChange={() => setSecondaria(i)} disabled={i === scelta} />
                          specifica
                        </label>
                      </td>
                    )}
                    {indici.map((j) => <td key={j}>{String(riga[j] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--cf-border)", display: "flex", justifyContent: "flex-end", gap: "10px", flexShrink: 0 }}>
          <button onClick={onAnnulla}>Annulla</button>
          <button className="btn-primario" onClick={confermaScelta}>Usa quest{dueRighe ? "e righe" : "a riga"}</button>
        </div>
      </div>
    </div>
  );
}
